import { Component, Input, ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { ListQueryComponent } from 'components/query/list-query.component';
import { InputType } from 'enums/InputType';
import { QueryType } from 'enums/QueryType';
import { TaskActionType } from 'enums/TaskActionType';
import { QueryModel } from 'models/generated/Entities.GormCrm.Query';
import { DynamicQueryModel, SelectPropertyModel, AggregationEnum } from 'models/generated/Models.DynamicQuery';
import { Comparison, ExpressionFilterModel } from 'models/generated/Models.ExpressionFilter';
import { QueryPropertyModel, QueryPropertyType, QueryPropertyValueType } from 'models/generated/Models.QueryProperty';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Inputs } from 'modules/inputs';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { QueryService, QueryResults, QueryBuilderMessage, QueryBuilderReply, QueryBuilderResponse, FilterValue } from 'services/query.service';
import { DynamicQueryValidationModel } from 'models/generated/Models.Query.DynamicQueryValidation';
import { RelationService } from 'services/relation.service';
import { QueryInputsComponent } from './query-inputs.component';

/**
 * The main query or one of its inline sub-queries, from an AI query builder reply or extracted from a saved query. Each one can be
 * opened in the editor. They reference each other with { queryRef: key } while being edited, and are inlined into the main query when saved.
 */
interface IEditableQuery {
	key: string;
	query: QueryModel;
	queryData: DynamicQueryModel;
	queryType: QueryType;
	validation?: DynamicQueryValidationModel;
}

@Component({
	selector: 'editquery',
	templateUrl: './edit-query.component.html',
	providers: [FormValidationService],
	imports: [Layout, Inputs, QueryInputsComponent, ListView, TranslatePipe, NgClass]
})
@ComponentUrl('edit-query')
export class EditQueryComponent {

	loading = true;

	public title = '';
	public query = new QueryModel();
	public showAiPrompt = false;
	showTab: 'filters' | 'selectProperties' | 'run' = 'filters';

	inputTypeEnum = InputType;
	queryTypeEnum = QueryType;
	taskActionTypeEnum = TaskActionType;
	aggregationTypeList: SelectListItemModel[];
	queryTypeList: SelectListItemModel[] = [];
	_queryData = new DynamicQueryModel();
	set queryData(data: DynamicQueryModel) {
		this._queryData = data;
	}
	get queryData() { return this._queryData; }

	queryProperties?: QueryPropertyModel[];
	queryPropertiesList: SelectListItemModel[] = [];
	queryType?: QueryType;

	selectPropertiesList: SelectListItemModel[] = [];

	operatorType = Comparison;
	queryPropertyType = QueryPropertyType;
	queryPropertyValueType = QueryPropertyValueType;
	results?: QueryResults[];
	savedQueries: QueryModel[] = [];

	/** The query the run tab executes: a copy of the active query with references to the other editable queries inlined. */
	runQueryData?: DynamicQueryModel;
	@ViewChild(QueryInputsComponent) queryInputs?: QueryInputsComponent;

	aiPrompt = '';
	aiHistory: QueryBuilderMessage[] = [];
	aiReply?: QueryBuilderReply;
	editableQueries: IEditableQuery[] = [];
	activeEditableQuery?: IEditableQuery;
	private mainQueryKey?: string;

	@UrlParam
	@Input()
	set queryId(id: string | undefined) {
		if (id != undefined) {
			this.clearEditableQueries();
			this.loading = true;
			this.queryService.getQuery(id).then(query => {
				this.query = query;
				this.loadQuery();
			});
		} else {
			this.loadQuery();
		}
	}
	get queryId() {
		return this.query && this.query.id ? this.query.id : '';
	}

	constructor(
		private readonly navigation: NavigationService,
		readonly queryService: QueryService,
		private readonly translate: TranslatePipe) {

		this.queryTypeList = this.toTranslatedList(QueryType.getKeys(), 'enum.query-type.');
		this.aggregationTypeList = this.toTranslatedList(AggregationEnum.getKeys(), 'enum.aggregation-type.');
	}

	private toTranslatedList<T extends number>(keys: Map<T, string>, languagePrefix: string): SelectListItemModel[] {
		return [...keys].map(([key, name]) => new SelectListItemModel(key, this.translate.transform(languagePrefix + name)));
	}

	availableFormatTypes(property: SelectPropertyModel): SelectListItemModel[] {
		return this.queryService.getAvailableFormatTypes(property).map(format => new SelectListItemModel(format.type, this.translate.transform('query.format.' + format.languageKey)));
	}

	async loadQuery() {
		this.loading = true;
		this.title = this.translate.transform(this.query.id ? 'query.edit-query' : 'query.create-query');
		this.queryData = new DynamicQueryModel();
		if (this.query.data != undefined && this.query.data != '') {
			this.queryData = RelationService.instantiateModelsFromData(new DynamicQueryModel(), Object.assign(this.queryData, JSON.parse(this.query.data)));
			this.queryType = this.queryData.queryType;
			await this.extractInlineQueries();
		} else {
			this.resetFilter();
		}
		await this.loadProperties();
		this.loading = false;
		if (this.showTab == 'run') this.showRunTab();
		this.queryService.getQueries().then(queries => this.savedQueries = queries);
		this.navigation.refreshUrl();
		this.navigation.openParentView(this, 'list-query', ListQueryComponent)
			.then(view => { view.selectedQuery = this.query; });
	}

	async loadProperties(resetFilters = false) {
		if (this.queryData && this.queryType != undefined) {
			if (this.queryData.queryType == undefined) this.queryData.queryType = this.queryType;
			if (resetFilters) this.resetFilter();

			this.selectPropertiesList = this.queryService.getPropertyDetails(this.queryType, this.queryData.selectProperties).map(p => { return new SelectListItemModel(p.derivedName, this.getPropertyLabel(p.derivedName, p.languageString!)); });

			if (this.queryData.selectProperties.length == 0) {
				const properties = this.queryService.getPropertyDetails(this.queryType);
				this.queryData.selectProperties = properties.filter(p => p.derivedName.toLowerCase().indexOf('id') == -1 && p.derivedName.toLowerCase().indexOf('delete') == -1 && p.derivedName.indexOf('.') == -1).map(p => {
					let property = new SelectPropertyModel()
					this.queryService.setSelectProperty(property, p, false);
					return property;
				});
			}

			const properties = await this.queryService.getQueryProperties(this.queryType);
			this.queryProperties = properties;
			this.queryService.resolveFilterProperties(this.queryData.filters, properties);
			this.queryPropertiesList = properties.map(property => new SelectListItemModel(property.name, this.getFilterPropertyLabel(property.name, this.queryType!)));
			this.queryData.filters.forEach(filter => this.setFilterOperatorsList(filter));
		}
	}

	private getFilterPropertyLabel(propertyName: string, queryType: QueryType): string {
		return this.translate.transform('query.' + this.queryService.getPropertyLanguageString(propertyName, QueryType[queryType]));
	}

	getPropertyLabel(databaseString: string, languageString: string): string {
		return (databaseString.indexOf('.') != -1 ? this.translate.transform('query.' + databaseString.split('.')[databaseString.split('.').length - 2].toLowerCase()) + ' > ' : '') + this.translate.transform('query.' + languageString);
	}

	addSelectProperty() {
		this.queryData.selectProperties.push(new SelectPropertyModel());
	}

	removeSelectProperty(property: SelectPropertyModel) {
		if (this.queryData.selectProperties.length == 1) return;
		const index = this.queryData.selectProperties.findIndex(p => p == property);
		this.queryData.selectProperties.splice(index, 1);
	}

	setSelectProperty(property: SelectPropertyModel, propertyName: string) {
		const properties = this.queryService.getPropertyDetails(this.queryType!, this.queryData.selectProperties);
		const isHidden = property?.isHidden ?? false;
		const aggregationType = property?.aggregationType;
		const newProperty = properties.find(p => p.derivedName == propertyName);
		if (newProperty)
			this.queryService.setSelectProperty(property, newProperty, isHidden, aggregationType);
	}

	resetFilter() {
		this.queryData.filters = [];
		this.addFilter();
	}

	addFilter() {
		const filter = new ExpressionFilterModel();
		filter.operatorsList = [];
		this.queryData.filters.push(filter);
	}
	setFilterProperty(filter: ExpressionFilterModel, propertyName: string) {
		filter.property = new QueryPropertyModel();
		filter.property.name = propertyName;
		this.queryService.resolveFilterProperties([filter], this.queryProperties!);
		this.setFilterOperatorsList(filter);
	}

	private setFilterOperatorsList(filter: ExpressionFilterModel) {
		const property = this.findQueryProperty(filter);
		if (!property) return;
		filter.operatorsList = QueryService.getComparisons(property)
			.map(comparison => new SelectListItemModel(comparison.toString(), this.translate.transform('query.operator.' + Comparison[comparison])));
	}

	private findQueryProperty(filter: ExpressionFilterModel) {
		return this.queryProperties?.find(property => property.name == filter.property?.name);
	}

	removeFilter(filter: ExpressionFilterModel) {
		if (this.queryData.filters.length == 1) return;
		const index = this.queryData.filters.findIndex(f => f == filter);
		this.queryData.filters.splice(index, 1);
	}

	async saveQuery() {
		if (!this.queryData || this.queryType == undefined) return;
		this.queryData.queryType = this.queryType!;

		const query = this.editableQueries.length > 0
			? await this.saveEditableQueries()
			: await this.queryService.saveQuery(this.query, this.queryData);
		this.navigation.openParentView(this, 'list-query', ListQueryComponent).then(view => view.updateQueryList());
		this.navigation.refreshView(EditQueryComponent).then(view => view.queryId = query.id ? query.id : '');
	}

	/** Saves the main query with every sub-query it references inlined, so it is saved as a single query. References to saved queries stay ids. */
	private async saveEditableQueries(): Promise<QueryModel> {
		this.storeActiveEditableQuery();
		const mainQuery = this.findEditableQuery(this.mainQueryKey)!;
		return await this.queryService.saveQuery(mainQuery.query, this.inlineQueryReferences(mainQuery.queryData, mainQuery.queryType, mainQuery.key));
	}

	/** A copy of the query data with every reference to another editable query replaced by an inline copy of it. */
	private inlineQueryReferences(queryData: DynamicQueryModel, queryType: QueryType, key: string | undefined): DynamicQueryModel {
		const copy = QueryService.cloneQueryData(queryData);
		copy.queryType = queryType;
		copy.filters.forEach(filter => filter.value = this.inlineQueryReference(filter.value, key ? [key] : []));
		return copy;
	}

	private inlineQueryReference(value: FilterValue, path: string[]): FilterValue {
		if (!QueryService.isQueryReference(value)) return value;
		const subQuery = this.findEditableQuery(value.queryRef);
		if (!subQuery || path.includes(subQuery.key)) throw new Error(`Invalid query reference "${value.queryRef}"`);
		return QueryService.toInlineQuery(subQuery.queryType, subQuery.queryData.filters,
			filter => this.inlineQueryReference(filter.value, [...path, subQuery.key]));
	}

	/**
	 * Moves the saved query's inline sub-queries into editable queries, recursively, so each can be opened in the editor, and replaces
	 * them with references that are inlined again when saving.
	 */
	private async extractInlineQueries() {
		const mainQuery: IEditableQuery = { key: 'main', query: this.query, queryData: this.queryData, queryType: this.queryType! };
		const editableQueries = [mainQuery];
		const extract = async (parent: IEditableQuery) => {
			for (const [index, filter] of parent.queryData.filters.entries()) {
				const value: FilterValue = filter.value;
				if (!QueryService.isInlineQuery(value)) continue;
				const query = new QueryModel();
				query.name = `${parent.query.name} › ${this.getFilterPropertyLabel(filter.property?.name ?? '', parent.queryType)}`;
				const { queryData } = await this.queryService.loadQueryData(value);
				const subQuery: IEditableQuery = { key: `${parent.key}/${index}`, query, queryData, queryType: queryData.queryType };
				filter.value = { queryRef: subQuery.key };
				editableQueries.push(subQuery);
				await extract(subQuery);
			}
		};
		await extract(mainQuery);
		if (editableQueries.length == 1) return;
		this.editableQueries = editableQueries;
		this.mainQueryKey = mainQuery.key;
		this.activeEditableQuery = mainQuery;
	}


	/** Opens the run tab, where the inputs of the query and its sub-queries are filled in before executing it. */
	showRunTab() {
		this.showTab = 'run';
		this.storeActiveEditableQuery();
		const runQueryData = this.inlineQueryReferences(this.queryData, this.queryType!, this.activeEditableQuery?.key);
		// Only replaced when the query changed, since a new query clears the values already filled in
		if (!this.runQueryData || RelationService.stringify(runQueryData) != RelationService.stringify(this.runQueryData)) this.runQueryData = runQueryData;
	}

	canShowRunTab() {
		return this.queryType != undefined && this.queryData.filters.some(filter => filter.property);
	}

	canExecute() {
		return !this.queryData.filters.some(filter => !filter.property?.name) && !!this.queryInputs?.isComplete;
	}

	async runQuery() {
		this.results = undefined;
		const queryData = await this.queryInputs?.getExecutableQuery();
		if (!queryData) return;
		// The type argument only labels the response; every query type is executed the same way
		this.results = await this.queryService.executeFilterQuery<QueryResults[]>(queryData);
	}

	get visibleSelectProperties() {
		return this.queryData.selectProperties.filter(property => !property.isHidden);
	}

	getOptions(filter: ExpressionFilterModel): { [key: string]: string } {
		return this.findQueryProperty(filter)?.options ?? {};
	}

	isQueryReference(value: unknown) {
		return QueryService.isQueryReference(value);
	}

	getFilterValueLabel(filter: ExpressionFilterModel): string {
		const value: FilterValue = filter.value;
		if (QueryService.isQueryReference(value)) return this.findEditableQuery(value.queryRef)?.query.name ?? value.queryRef;
		if (value == null || QueryService.isInlineQuery(value)) return '';
		if (filter.property?.type == QueryPropertyType.Query) return this.savedQueries.find(query => query.id == value)?.name ?? String(value);
		if (filter.property?.type == QueryPropertyType.Select) return this.getOptions(filter)[String(value)] ?? String(value);
		return String(value);
	}

	sendPrompt = async () => {
		const prompt = this.aiPrompt.trim();
		if (!prompt) return;
		const response = await this.queryService.buildQuery(prompt, this.aiHistory);
		this.aiHistory = [...this.aiHistory, { role: 'user', content: prompt }, { role: 'assistant', content: JSON.stringify(response.reply) }];
		this.aiReply = response.reply;
		if (response.reply.queries?.length > 0) await this.loadGeneratedQueries(response);
	};

	private async loadGeneratedQueries(response: QueryBuilderResponse) {
		const editableQueries: IEditableQuery[] = [];
		for (const namedQuery of response.reply.queries) {
			const query = new QueryModel();
			query.name = namedQuery.name;
			query.description = namedQuery.description;
			const { queryData } = await this.queryService.loadQueryData(namedQuery.data);
			editableQueries.push({
				key: namedQuery.key,
				query,
				queryData,
				queryType: queryData.queryType,
				validation: response.dryRuns?.find(dryRun => dryRun.key == namedQuery.key)?.validation,
			});
		}
		this.editableQueries = editableQueries;
		this.mainQueryKey = (this.findEditableQuery(response.reply.mainQuery) ?? editableQueries[editableQueries.length - 1]).key;
		this.activeEditableQuery = undefined;
		await this.showEditableQuery(this.findEditableQuery(this.mainQueryKey)!);
	}

	async showEditableQuery(editableQuery: IEditableQuery) {
		this.storeActiveEditableQuery();
		this.activeEditableQuery = editableQuery;
		this.query = editableQuery.query;
		this.queryData = editableQuery.queryData;
		this.queryType = editableQuery.queryType;
		this.results = undefined;
		await this.loadProperties();
		if (this.showTab == 'run') this.showRunTab();
	}

	showReferencedQuery(filter: ExpressionFilterModel) {
		const editableQuery = QueryService.isQueryReference(filter.value) ? this.findEditableQuery(filter.value.queryRef) : undefined;
		if (editableQuery) this.showEditableQuery(editableQuery);
	}

	private storeActiveEditableQuery() {
		if (!this.activeEditableQuery) return;
		this.activeEditableQuery.queryData = this.queryData;
		this.activeEditableQuery.queryType = this.queryType!;
	}

	private clearEditableQueries() {
		this.aiPrompt = '';
		this.aiHistory = [];
		this.aiReply = undefined;
		this.editableQueries = [];
		this.activeEditableQuery = undefined;
		this.mainQueryKey = undefined;
	}

	private findEditableQuery(key: string | null | undefined) {
		return this.editableQueries.find(editableQuery => editableQuery.key == key);
	}

}
